import React, { useCallback, useContext, useMemo } from 'react';
import styled from 'styled-components';
import _ from 'lodash';

import Row from 'components/Common/Row';
import Card from 'components/Common/Card';
import Divider from 'components/Common/Card/Divider';
import Loading from 'components/Loading/Loading';
import Typography from 'kingpin/atoms/Typography';
import useGetFieldLabel from 'hooks/useGetFieldLabel';
import useValueFormatters from 'hooks/useValueFormatters';
import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import BonusPeriodsContext from 'contexts/BonusPeriodsContext';
import BaseViewsContext from 'contexts/BaseViewsContext';
import getBaseViewFields from 'components/Grid/getBaseViewFields';
import resolveExpectationField from 'components/DriverAX/utils/resolveExpectationField';
import computeExpectationIsPassed from 'components/DriverAX/utils/computeExpectationIsPassed';
import ExpectationValue from 'components/DriverAX/components/ExpectationValue';
import { BonusOverviewContext } from 'components/DriverAX/providers/BonusOverviewProvider';
import { WeeklyBonusOverrideContext } from 'components/DriverAX/providers/WeeklyBonusOverrideProvider';
import SelectedEntityContext from 'components/PerformanceBoards/contexts/SelectedEntityContext';
import BonusOverrideIndicator from 'components/PerformanceBoards/BonusOverrideIndicator';

import resolveHeaderTotalField from '../../headerTotalField';

import useBonusSubPeriodRows, {
  BonusSubPeriodRow,
} from '../../../hooks/useBonusSubPeriodRows';
import WeeklyRewardOverrideCell from './WeeklyRewardOverrideCell';
import WeeklyEligibilityOverrideCell from './WeeklyEligibilityOverrideCell';
import WeeklyClearOverrideControl from './WeeklyClearOverrideControl';
import resolveCellOverrides, {
  resolveOverrideRows,
} from './resolveCellOverrides';
import resolveOverridableRewardName, {
  ELIGIBILITY_FIELD,
} from './overridableRewardName';

// Above 2 fields the columns-per-field table gets too wide for the slideout, so
// we switch to a stacked per-week layout.
const MAX_TABLE_FIELDS = 2;

const Col = styled.div<{ alignRight?: boolean }>`
  flex: 1;
  margin-right: 16px;
  text-align: ${(props) => (props.alignRight ? 'right' : 'left')};
  word-wrap: break-word;
`;

// Sub-period labels carry the year (e.g. "Aug 09 - Aug 15 2026"); drop it in the
// weekly breakdown, where the year is redundant and makes the Period column wrap.
const stripYear = (label: string) => label.replace(/\s+\d{4}$/, '');

/**
 * Per-week breakdown of configured bonus dataset fields, shown for the Bonus
 * Reward / Bonus Total Reward / Bonus Eligibility slideout sections of programmes
 * that use sub-periods. Self-hides for ordinary programmes and when no fields are
 * configured. Weekly rows come from useBonusSubPeriodRows (shared with the
 * section header). `title`/`showTotal` render an optional header with the summed
 * reward total (used by the Bonus Total Reward section).
 */
const BonusSubPeriodFactTable = ({
  bonusConfigId,
  fields,
  rewardName,
  showTotal,
  title,
  totalFieldOverride,
  renderMode = 'auto',
}: {
  bonusConfigId: string;
  fields: string[] | undefined;
  // The reward this section is for (Bonus Reward section). Omitted for the Bonus
  // Total Reward section, which totals the programme's overall reward field.
  rewardName?: string;
  // Show the summed total next to the title header.
  showTotal?: boolean;
  // Header label above the table (e.g. the reward name). No header when omitted.
  title?: string;
  // Overrides which dataset field the summed total uses/formats (e.g. `totalPay` $
  // instead of the programme's `total` points field). Falls back to the reward field.
  totalFieldOverride?: string;
  // Layout override: `auto` (default) uses the table for ≤2 fields and stacked
  // cards above that; `table`/`cards` force that layout.
  renderMode?: 'auto' | 'table' | 'cards';
}) => {
  const { bonusPrograms } = useContext(DatasetDefinitionsContext);
  const { selectedSubPeriod } = useContext(BonusPeriodsContext);
  const { baseViews } = useContext(BaseViewsContext);
  const { currentMode } = useContext(BonusOverviewContext);
  const {
    weeks: overrideWeeks,
    isLoading: isOverrideLoading,
    stageWeekCriteriaClear,
    stageWeekReward,
  } = useContext(WeeklyBonusOverrideContext);
  const { selectedEntity } = useContext(SelectedEntityContext);
  const {
    rows,
    dataType,
    isLoading: isRowsLoading,
  } = useBonusSubPeriodRows(bonusConfigId);

  const { formatField } = useValueFormatters();
  const { getFieldLabel } = useGetFieldLabel();

  const program = useMemo(
    () => bonusPrograms.find((p) => p.id === bonusConfigId),
    [bonusPrograms, bonusConfigId],
  );

  const isOverrideMode = currentMode.mode === 'override';

  // One loading state for the whole table: the weekly grid rows AND, in override
  // mode, the per-week records + options. Showing the single top spinner until
  // both are ready avoids painting plain values and then flipping each pay cell
  // to a dropdown.
  const isLoading = isRowsLoading || (isOverrideMode && isOverrideLoading);

  // The reward a pay column maps to (e.g. safetyBonusPay → safetyBonus), or
  // undefined for a non-pay column. A configured pay column is what becomes a
  // per-week override dropdown in override mode.
  const overridableRewardName = useCallback(
    (field: string): string | undefined =>
      resolveOverridableRewardName({ field, program }),
    [program],
  );

  // Join a fact-table row to its loaded override week by DATE range (the same way
  // rows are matched to sub-periods). The Elastic grid docs don't carry the
  // reward record id, so we can't join on it — the week's own bonusDatasetRecordId
  // is the authoritative key for options / preview / PUT.
  const weekForRow = useCallback(
    (row: BonusSubPeriodRow) => {
      const date = row.doc?.['date'];
      if (typeof date !== 'string') {
        return undefined;
      }
      return overrideWeeks.find(
        (w) => date >= w.startDate && date <= w.endDate,
      );
    },
    [overrideWeeks],
  );

  // A pay field's weekly value, reflecting the per-week preview when the reward is
  // staged for override (so figures/totals update live before Confirm), else the
  // Elastic doc value.
  const resolveWeekFieldValue = useCallback(
    (row: BonusSubPeriodRow, field: string): number | string | undefined => {
      const rewardName = overridableRewardName(field);
      const week = weekForRow(row);
      if (rewardName && week?.preview) {
        const previewReward = week.preview.rewards.find(
          (r) => r.name === rewardName,
        )?.reward;
        if (previewReward !== undefined) {
          return previewReward;
        }
      }
      return row.doc?.[field];
    },
    [overridableRewardName, weekForRow],
  );

  // Renders a field's weekly value. When the field maps to a program
  // criteria/qualifier/disqualifier (e.g. `mpg` → `mpgTarget`/`mpgStatus`), show
  // the `actual / target` pair coloured by the backend-computed status (neutral
  // when the status is blank). Otherwise show the plain formatted value.
  const renderBaseFieldValue = useCallback(
    (row: BonusSubPeriodRow, field: string): React.ReactNode => {
      const dataset = dataType ?? '';

      // The eligibility column becomes a per-week Qualified/Disqualified dropdown
      // (a qualifier override) in override mode.
      if (isOverrideMode && field === ELIGIBILITY_FIELD && program) {
        const week = weekForRow(row);
        if (week) {
          return (
            <WeeklyEligibilityOverrideCell
              bonusDatasetRecordId={week.bonusDatasetRecordId}
            />
          );
        }
      }

      const expectation = resolveExpectationField({ program, field });
      if (!expectation) {
        const formatted = formatField({
          value: resolveWeekFieldValue(row, field),
          field,
          dataset,
        });
        // In override mode a configured pay column becomes a per-week payout
        // dropdown (fanned out as one override PUT per week on Confirm). The table
        // only renders once the override data has loaded (see isLoading), so the
        // cell resolves straight to a dropdown with no per-cell spinner.
        const rewardName = overridableRewardName(field);
        if (isOverrideMode && rewardName && program) {
          const week = weekForRow(row);
          if (week && week.options.has(rewardName) && !week.optionsFailed) {
            return (
              <WeeklyRewardOverrideCell
                bonusDatasetRecordId={week.bonusDatasetRecordId}
                rewardName={rewardName}
                field={field}
                bonusProgram={program}
                label={String(formatted)}
              />
            );
          }
          // No payout options for the reward this week, but a persisted override
          // can still be removed from its row.
          if (
            week &&
            week.actual.rewardOverrides?.some(
              (o) => o.rewardName === rewardName,
            )
          ) {
            const isCleared = week.staged.some(
              (o) => o.rewardName === rewardName && o.action === 'Clear',
            );
            return (
              <Row centerAlign style={{ gap: 4, justifyContent: 'flex-end' }}>
                {formatted}
                <WeeklyClearOverrideControl
                  isCleared={isCleared}
                  onClear={() =>
                    stageWeekReward({
                      bonusDatasetRecordId: week.bonusDatasetRecordId,
                      rewardName,
                      override: { action: 'Clear', rewardName },
                    })
                  }
                  onUndo={() =>
                    stageWeekReward({
                      bonusDatasetRecordId: week.bonusDatasetRecordId,
                      rewardName,
                      override: undefined,
                    })
                  }
                  testId={`weekly-reward-clear-${week.bonusDatasetRecordId}-${rewardName}`}
                />
              </Row>
            );
          }
        }
        return formatted;
      }
      const actual = formatField({
        value: row.doc?.[expectation.actualField],
        field: expectation.actualField,
        dataset,
      });
      const target = formatField({
        value: row.doc?.[expectation.targetField],
        field: expectation.targetField,
        dataset,
      });
      const status = row.doc?.[expectation.statusField];
      const baseView = baseViews[dataset];
      const belowTargetIsGood = baseView
        ? getBaseViewFields(baseView).find((f) => f.field === field)
            ?.belowTargetIsGood
        : undefined;
      const isPassed = computeExpectationIsPassed({
        status,
        belowTargetIsGood,
      });
      const expectationValue = (
        <ExpectationValue actual={actual} target={target} isPassed={isPassed} />
      );

      // In override mode a week's persisted criteria override can be removed
      // from its actual/target cell.
      if (isOverrideMode) {
        const week = weekForRow(row);
        const criterionField = week?.persisted?.criteriaOverrides.find(
          (o) =>
            o.criterionField === expectation.actualField ||
            o.criterionField === field,
        )?.criterionField;
        if (week && criterionField) {
          return (
            <Row centerAlign style={{ gap: 4, justifyContent: 'flex-end' }}>
              {expectationValue}
              <WeeklyClearOverrideControl
                isCleared={week.stagedCriteriaClears.includes(criterionField)}
                onClear={() =>
                  stageWeekCriteriaClear({
                    bonusDatasetRecordId: week.bonusDatasetRecordId,
                    criterionField,
                    isCleared: true,
                  })
                }
                onUndo={() =>
                  stageWeekCriteriaClear({
                    bonusDatasetRecordId: week.bonusDatasetRecordId,
                    criterionField,
                    isCleared: false,
                  })
                }
                testId={`weekly-criteria-clear-${week.bonusDatasetRecordId}-${criterionField}`}
              />
            </Row>
          );
        }
      }
      return expectationValue;
    },
    [
      program,
      dataType,
      formatField,
      baseViews,
      resolveWeekFieldValue,
      overridableRewardName,
      weekForRow,
      isOverrideMode,
      stageWeekCriteriaClear,
      stageWeekReward,
    ],
  );

  // A field's weekly value plus, when the week's record has overrides on that
  // item (backend `_overrides` on the weekly doc), a warning indicator whose
  // tooltip shows who overrode it, when and why. Shown in both modes.
  const renderFieldValue = useCallback(
    (row: BonusSubPeriodRow, field: string): React.ReactNode => {
      const value = renderBaseFieldValue(row, field);
      const cellOverrides = resolveCellOverrides({
        doc: row.doc,
        field,
        program,
      });
      if (cellOverrides.length === 0) {
        return value;
      }
      return (
        <Row centerAlign style={{ gap: 4, justifyContent: 'flex-end' }}>
          {value}
          <BonusOverrideIndicator
            driver={selectedEntity ?? ''}
            bonusOverridesMetaData={cellOverrides}
            testId={`fact-override-indicator-${row.key}-${field}`}
          />
        </Row>
      );
    },
    [renderBaseFieldValue, program, selectedEntity],
  );

  // Per week, which override rows to add (overrides on items the board has no
  // column for) and which overrides/exclusions only fit on the week label.
  const overrideRowsByKey = useMemo(
    () =>
      new Map(
        rows.map((row) => [
          row.key,
          resolveOverrideRows({ doc: row.doc, fields: fields ?? [], program }),
        ]),
      ),
    [rows, fields, program],
  );

  // Table layout: the extra override columns across all weeks, so each week's
  // override sits in its own column.
  const tableExtraFields = useMemo(
    () =>
      _.uniq(
        Array.from(overrideRowsByKey.values()).flatMap((r) => r.extraFields),
      ),
    [overrideRowsByKey],
  );

  // A week's label plus, when the week has overrides that can't be tied to a
  // row (or exclusions), an indicator listing them. Overrides on an item are
  // flagged on that item's row instead (see overrideRowsByKey).
  const renderWeekLabel = useCallback(
    (row: BonusSubPeriodRow): React.ReactNode => {
      const label = stripYear(row.label);
      const placement = overrideRowsByKey.get(row.key);
      const unplaced = placement?.unplaced ?? [];
      const exclusions = placement?.exclusions ?? [];
      if (unplaced.length === 0 && exclusions.length === 0) {
        return label;
      }
      return (
        <Row centerAlign style={{ gap: 4 }}>
          {label}
          <BonusOverrideIndicator
            driver={selectedEntity ?? ''}
            bonusOverridesMetaData={unplaced}
            bonusExclusionsMetaData={exclusions}
            testId={`fact-week-override-indicator-${row.key}`}
          />
        </Row>
      );
    },
    [overrideRowsByKey, selectedEntity],
  );

  // The dataset field whose weekly values sum to the total: an explicit override
  // (e.g. `totalPay`, or "None" to hide), else the named reward's pay field, or the
  // programme's overall reward field (name 'total').
  const totalRewardField = useMemo(() => {
    if (!showTotal) {
      return undefined;
    }
    const program = bonusPrograms.find((p) => p.id === bonusConfigId);
    const fallbackField = program?.rewardFields.find((f) =>
      rewardName ? f.name === rewardName : f.name === 'total',
    )?.rewardValueField;
    return resolveHeaderTotalField({
      totalValueField: totalFieldOverride,
      fallbackField,
    });
  }, [showTotal, totalFieldOverride, bonusPrograms, bonusConfigId, rewardName]);

  const total = useMemo(() => {
    if (!totalRewardField) {
      return undefined;
    }
    return rows.reduce((sum, row) => {
      const value = resolveWeekFieldValue(row, totalRewardField);
      return typeof value === 'number' ? sum + value : sum;
    }, 0);
  }, [rows, totalRewardField, resolveWeekFieldValue]);

  if (!dataType || rows.length === 0 || !fields?.length) {
    return null;
  }

  const isStacked =
    renderMode === 'auto'
      ? fields.length > MAX_TABLE_FIELDS
      : renderMode === 'cards';

  return (
    <div style={{ marginTop: 16 }} data-testid={'bonus-sub-period-fact-table'}>
      {title && (
        <Row
          spaceBetween
          centerAlign
          style={{ marginBottom: 8 }}
          data-testid={'fact-header'}
        >
          <Typography.Header type="H5">{title}</Typography.Header>
          {showTotal && totalRewardField && total !== undefined && (
            <Typography.Header type="H5">
              {formatField({
                value: total,
                field: totalRewardField,
                dataset: dataType,
              })}
            </Typography.Header>
          )}
        </Row>
      )}

      {isLoading && (
        <div
          style={{ height: 80, display: 'flex' }}
          data-testid={'fact-table-loading'}
        >
          <Loading isSmall />
        </div>
      )}

      {!isLoading && isStacked && (
        <div>
          {rows.map((row) => (
            <Card
              key={row.key}
              style={{
                padding: 8,
                marginBottom: 8,
                border:
                  row.key === selectedSubPeriod?.startDate
                    ? '1px solid var(--color-info-v2)'
                    : undefined,
              }}
              data-testid={'fact-week-card'}
            >
              <Typography.Body type="Body 14">
                {renderWeekLabel(row)}
              </Typography.Body>
              <Divider style={{ marginTop: 6, marginBottom: 8 }} />
              {[
                ...fields,
                ...(overrideRowsByKey.get(row.key)?.extraFields ?? []),
              ].map((field) => (
                <Row
                  key={field}
                  spaceBetween
                  centerAlign
                  style={{ padding: '4px 0' }}
                >
                  <Typography.Body type="Label">
                    {getFieldLabel({ field, dataType })}
                  </Typography.Body>
                  <Typography.Body type="Body 12">
                    {renderFieldValue(row, field)}
                  </Typography.Body>
                </Row>
              ))}
            </Card>
          ))}
        </div>
      )}

      {!isLoading && !isStacked && (
        <Card
          style={{
            padding: 8,
            paddingTop: 0,
          }}
        >
          <Row
            centerAlign
            style={{
              padding: 8,
              boxShadow: 'inset 0px -1px 0px rgba(0, 0, 0, 0.12)',
              backgroundColor: 'var(--color-bg-primary)',
              minWidth: 'fit-content',
            }}
          >
            <Col>
              <Typography.Body type="Label">Period</Typography.Body>
            </Col>
            {[...fields, ...tableExtraFields].map((field) => (
              <Col key={field} alignRight>
                <Typography.Body type="Label">
                  {getFieldLabel({ field, dataType })}
                </Typography.Body>
              </Col>
            ))}
          </Row>
          {rows.map((row) => (
            <Row
              key={row.key}
              centerAlign
              style={{
                boxShadow: 'inset 0px -1px 0px rgba(0, 0, 0, 0.08)',
                padding: 4,
                minHeight: 32,
                minWidth: 'fit-content',
                backgroundColor:
                  row.key === selectedSubPeriod?.startDate
                    ? 'var(--color-grid-selected-row)'
                    : undefined,
              }}
            >
              <Col>
                <Typography.Body type="Body 12">
                  {renderWeekLabel(row)}
                </Typography.Body>
              </Col>
              {[...fields, ...tableExtraFields].map((field) => (
                <Col key={field} alignRight>
                  <Typography.Body type="Body 12">
                    {renderFieldValue(row, field)}
                  </Typography.Body>
                </Col>
              ))}
            </Row>
          ))}
        </Card>
      )}
    </div>
  );
};

export default BonusSubPeriodFactTable;
